import { useEffect, useRef } from "react";

// ── Vertex shader ─────────────────────────────────────────────────────────────
const VERT_SRC = `
attribute vec2 a_position;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
}
`;

// ── Fragment shader ───────────────────────────────────────────────────────────
// Iridescent fluid via layered FBM + hue cycling
const FRAG_SRC = `
precision highp float;
uniform float u_time;
uniform vec2  u_resolution;

// HSL → RGB
vec3 hsl2rgb(vec3 c) {
  vec3 rgb = clamp(abs(mod(c.x * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
  return c.z + c.y * (rgb - 0.5) * (1.0 - abs(2.0 * c.z - 1.0));
}

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i),              hash(i + vec2(1.0, 0.0)), f.x),
    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x),
    f.y
  );
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 6; i++) {
    v += a * noise(p);
    p  = p * 2.1 + vec2(1.7, 9.2);
    a *= 0.5;
  }
  return v;
}

void main() {
  vec2 uv = gl_FragCoord.xy / u_resolution;
  float t  = u_time * 0.10;

  // Two layers of warped FBM for rich detail
  vec2 p = uv * 2.8;
  vec2 q = vec2(fbm(p + t * 0.30),           fbm(p + vec2(5.2, 1.3) + t * 0.25));
  vec2 r = vec2(fbm(p + 4.0 * q + vec2(1.7, 9.2) + t * 0.15),
                fbm(p + 4.0 * q + vec2(8.3, 2.8) + t * 0.15));

  float f = fbm(p + 4.0 * r);

  // Iridescent hue: hue shifts across space & time
  float hue = fract(f * 2.2 + t * 0.07 + uv.x * 0.30 + uv.y * 0.20);
  float sat = 0.70 + 0.30 * sin(f * 6.2832 + t * 1.5);
  float lum = 0.28 + 0.22 * f + 0.08 * sin(t + uv.y * 3.0);

  vec3 col = hsl2rgb(vec3(hue, sat, lum));

  // Subtle specular shimmer
  col += 0.06 * vec3(1.0) * pow(max(0.0, sin(f * 18.0 + t * 3.0)), 6.0);

  // Soft vignette
  float vig = 1.0 - 0.45 * dot(uv - 0.5, uv - 0.5) * 2.5;
  col *= vig;

  gl_FragColor = vec4(col, 1.0);
}
`;

// ── WebGL helpers ─────────────────────────────────────────────────────────────
function createShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader | null {
  const s = gl.createShader(type);
  if (!s) return null;
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    console.error("Shader compile error:", gl.getShaderInfoLog(s));
    gl.deleteShader(s);
    return null;
  }
  return s;
}

function createProgram(gl: WebGLRenderingContext, vert: string, frag: string): WebGLProgram | null {
  const vs = createShader(gl, gl.VERTEX_SHADER, vert);
  const fs = createShader(gl, gl.FRAGMENT_SHADER, frag);
  if (!vs || !fs) return null;
  const prog = gl.createProgram();
  if (!prog) return null;
  gl.attachShader(prog, vs);
  gl.attachShader(prog, fs);
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
    console.error("Program link error:", gl.getProgramInfoLog(prog));
    return null;
  }
  return prog;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function WebGLIridescentBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef    = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", { alpha: false, antialias: false });
    if (!gl) {
      // WebGL not supported — leave the canvas transparent (CSS fallback will show)
      return;
    }

    const program = createProgram(gl, VERT_SRC, FRAG_SRC);
    if (!program) return;

    // Full-screen quad
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
      gl.STATIC_DRAW
    );

    const posLoc  = gl.getAttribLocation(program, "a_position");
    const timeLoc = gl.getUniformLocation(program, "u_time");
    const resLoc  = gl.getUniformLocation(program, "u_resolution");

    if (gl.isContextLost()) return;
      gl.useProgram(program);
    gl.enableVertexAttribArray(posLoc);
    gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

    let start: number | null = null;

    const resize = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      gl.viewport(0, 0, canvas.width, canvas.height);
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    const render = (ts: number) => {
      if (start === null) start = ts;
      const t = (ts - start) / 1000;

      gl.uniform1f(timeLoc, t);
      gl.uniform2f(resLoc, canvas.width, canvas.height);
      gl.drawArrays(gl.TRIANGLES, 0, 6);

      rafRef.current = requestAnimationFrame(render);
    };

    rafRef.current = requestAnimationFrame(render);

    return () => {
      const ext = gl.getExtension('WEBGL_lose_context');
      if (ext) ext.loseContext();
      cancelAnimationFrame(rafRef.current);
      observer.disconnect();
      gl.deleteBuffer(buf);
      gl.deleteProgram(program);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}
